import { useLocation, useParams } from '@tanstack/react-router';
import { AnimatePresence, motion } from 'motion/react';
import { useEffect, useRef, useState } from 'react';
import { useIntlayer } from 'react-intlayer';

import { LocalizedLink } from '~/components/LocalizedLink';
import { Menu } from '~/components/Menu';
import { Button } from '~/components/ui/button';
import { HamburgerIcon } from '~/components/ui/HamburgerIcon';
import { ArrowUpRight, Chevron } from '~/components/ui/icons';
import { SOCIAL_LINKS } from '~/constants';
import {
  HEADER_COMPANY_ITEMS,
  HEADER_NAV_ITEMS,
  type HeaderCompanyNavItemKey,
  type HeaderNavItemKey,
} from '~/constants/navigation';
import { openExternal } from '~/utils/open-external';

const normalizePath = (path: string) =>
  path.length > 1 && path.endsWith('/') ? path.slice(0, -1) : path;

const buildLocalizedPathVariants = (path: string, locale?: string) => {
  const normalized = path.startsWith('/') ? path : `/${path}`;
  const variants = [normalizePath(normalized)];

  if (locale) {
    variants.push(
      normalizePath(
        normalized === '/' ? `/${locale}` : `/${locale}${normalized}`
      )
    );
  }

  return variants;
};

const SubmenuArrow = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 18 18"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path
      d="M15.2148 9.75015L4.87891 9.74985V4.42969M15.2148 9.75015L11.6674 6.20287M15.2148 9.75015L11.6674 13.2971"
      stroke="currentColor"
      strokeWidth="1.25"
      strokeMiterlimit="10"
    />
  </svg>
);

type CompanyMenuButtonProps = {
  label: string;
  navigation: Record<HeaderCompanyNavItemKey | 'company', string>;
};

const CompanyMenuButton = ({ label, navigation }: CompanyMenuButtonProps) => {
  const [isCompanyMenuOpen, setIsCompanyMenuOpen] = useState(false);
  const companyMenuRef = useRef<HTMLDivElement>(null);
  const { pathname } = useLocation();
  const routeParams = useParams({ strict: false });

  const rawLocale = (routeParams as Record<string, string>)?.locale;

  const normalizedPathname = normalizePath(pathname);

  useEffect(() => {
    const handlePointerDown = (event: MouseEvent) => {
      if (!companyMenuRef.current?.contains(event.target as Node)) {
        setIsCompanyMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, []);

  return (
    <div ref={companyMenuRef} className="relative">
      <button
        type="button"
        className="inline-flex items-center cursor-pointer justify-center gap-2 px-[10px] py-[5px] font-manrope text-[16px] font-normal uppercase leading-6 tracking-[0.16px] transition-colors duration-200 text-white hover:text-blue-primary focus-visible:text-blue-primary"
        aria-expanded={isCompanyMenuOpen}
        onClick={() => setIsCompanyMenuOpen((prev) => !prev)}
      >
        {label}
        <Chevron
          className={`size-4 transition ${isCompanyMenuOpen ? 'rotate-180' : ''} `}
        />
      </button>
      <AnimatePresence>
        {isCompanyMenuOpen ? (
          <motion.div
            initial={{ clipPath: 'inset(0 0 100% 0)' }}
            animate={{ clipPath: 'inset(0 0 0% 0)' }}
            exit={{ clipPath: 'inset(0 0 100% 0)' }}
            transition={{ duration: 0.25, ease: 'easeInOut' }}
            className="absolute left-0 top-full z-10 flex min-w-[240px] flex-col bg-black pt-2"
          >
            {HEADER_COMPANY_ITEMS.map((item) => {
              return (
                <LocalizedLink
                  key={item.key}
                  to={item.to}
                  className="flex w-full items-center gap-2 p-3 transition-colors duration-200 text-white hover:bg-blue-primary"
                  onClick={() => setIsCompanyMenuOpen(false)}
                >
                  <SubmenuArrow />
                  <span className="font-fragment-mono text-[14px] leading-[1.5] normal-case">
                    {navigation[item.key]}
                  </span>
                </LocalizedLink>
              );
            })}
          </motion.div>
        ) : null}
      </AnimatePresence>
    </div>
  );
};

export const Header = () => {
  const header = useIntlayer('header') as {
    joinTeam: string;
  };
  const { pathname } = useLocation();
  const routeParams = useParams({ strict: false });
  const navigation = useIntlayer('navigation') as Record<
    HeaderNavItemKey | HeaderCompanyNavItemKey | 'company',
    string
  >;
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 0);
    };

    // Check initial scroll position on mount
    handleScroll();

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  /* Lock body scroll when menu is open */
  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMenuOpen]);

  /* Close menu when viewport crosses the md breakpoint (768px) */
  useEffect(() => {
    const mql = window.matchMedia('(min-width: 768px)');
    const handleChange = (e: MediaQueryListEvent) => {
      if (e.matches) setIsMenuOpen(false);
    };
    mql.addEventListener('change', handleChange);
    return () => mql.removeEventListener('change', handleChange);
  }, []);

  return (
    <>
      <header
        className={`flex items-center px-4 py-4 md:px-8 md:py-[10px] lg:px-20 lg:py-6 w-full fixed top-0 z-50 transition-colors duration-100 ${isScrolled || isMenuOpen ? 'bg-black' : ''} max-w-[1680px] mx-auto`}
      >
        <div className="flex items-center justify-between w-full">
          <LocalizedLink to="/" className="flex items-center shrink-0">
            <img
              src="/assets/images/trypillian-logo-white-horizontal.svg"
              alt="Logo"
              className="h-5 w-auto md:h-6"
            />
          </LocalizedLink>

          {/* Navigation - Desktop only */}
          <nav className="hidden lg:flex items-center gap-[10px] lg:gap-[36px]">
            {HEADER_NAV_ITEMS.map((item) => {
              return (
                <LocalizedLink
                  key={item.key}
                  to={item.to}
                  className="inline-flex items-center cursor-pointer justify-center gap-2 px-[10px] py-[5px] font-manrope text-[16px] font-normal uppercase leading-6 tracking-[0.16px] transition-colors duration-200 whitespace-nowrap text-white hover:text-blue-primary focus-visible:text-blue-primary"
                >
                  {navigation[item.key]}
                </LocalizedLink>
              );
            })}

            <CompanyMenuButton
              label={navigation.company}
              navigation={navigation}
            />
          </nav>

          {/* Right side actions */}
          <div className="flex items-center gap-4">
            {/* Join Team - Desktop only */}
            <Button
              variant="default"
              size="sm"
              className="hidden lg:inline-flex"
              onClick={(e) => {
                e.preventDefault();
                openExternal(SOCIAL_LINKS.LINKEDIN);
              }}
            >
              {header.joinTeam}
              <ArrowUpRight />
            </Button>

            <HamburgerIcon
              isOpen={isMenuOpen}
              onClick={() => setIsMenuOpen((prev) => !prev)}
            />
          </div>
        </div>
      </header>

      {/* Menu renders outside header so z-index layers correctly */}
      <Menu isOpen={isMenuOpen} onClose={() => setIsMenuOpen(false)} />
    </>
  );
};
